import { useTranslation } from 'react-i18next'
import { BellRing, Clock3, Newspaper } from 'lucide-react'
import { useTheme } from '../../../shared/hooks/useTheme'
import { cn } from '@/lib/utils'
import { AreaChart } from './AreaChart'
import { BorderBeam } from './BorderBeam'
import { glassCard, innerCard } from './landingStyles'
import { useScrollReveal } from './useScrollReveal'

const trendData = [18, 24, 22, 30, 28, 35, 40, 38, 44, 46, 42, 48, 52, 50, 56]
const volumeData = [32, 28, 35, 42, 38, 45, 40, 48, 52, 46, 55, 58, 54, 62, 60]

const sentimentBars = [
  { labelKey: '1', value: 52, color: '#34d399' },
  { labelKey: '2', value: 31, color: '#818cf8' },
  { labelKey: '3', value: 17, color: '#fb7185' },
] as const

const keywords = [
  { key: '#election', mentions: '2.4K', delta: '+12%' },
  { key: '#inflation', mentions: '1.9K', delta: '+7%' },
  { key: '#policy', mentions: '1.3K', delta: '+4%' },
] as const

export function AnalyticsPreviewSection() {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { ref, isVisible } = useScrollReveal()

  const isDark = theme === 'dark'

  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div ref={ref} className={cn('scroll-reveal', isVisible && 'is-visible')}>
        <div className="mb-10 max-w-2xl">
          <h2 className="m-0 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {t('landing.dashboard.title')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {t('landing.dashboard.subtitle')}
          </p>
        </div>

        {/* Mobile: simplified single stat card */}
        <div className="lg:hidden">
          <div className={cn(glassCard, 'p-5')}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="m-0 text-sm font-medium text-muted-foreground">
                {t('landing.dashboard.keywordTrend')}
              </h3>
              <span className="rounded-full bg-badge px-2 py-0.5 text-[10px] font-medium text-badge-foreground">
                {t('landing.dashboard.badges.nearRealtime')}
              </span>
            </div>
            <AreaChart data={trendData} height={120} />
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className={cn(innerCard, 'p-3')}>
                <div className="text-[11px] text-muted-foreground">
                  {t('landing.dashboard.cards.mentions')}
                </div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {t('landing.dashboard.cards.mentionsValue')}
                </div>
              </div>
              <div className={cn(innerCard, 'p-3')}>
                <div className="text-[11px] text-muted-foreground">
                  {t('landing.dashboard.cards.activeSources')}
                </div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {t('landing.dashboard.cards.activeSourcesValue')}
                </div>
              </div>
              <div className={cn(innerCard, 'p-3')}>
                <div className="text-[11px] text-muted-foreground">
                  {t('landing.dashboard.cards.freshness')}
                </div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {t('landing.dashboard.cards.freshnessValue')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop: full dashboard preview */}
        <div className="hidden gap-4 lg:grid lg:grid-cols-[1.2fr_0.8fr]">
          {/* Main chart panel */}
          <div className={cn(glassCard, 'relative overflow-hidden p-6')}>
            {isDark && <BorderBeam duration="8s" />}
            <div className="mb-5 flex items-center justify-between">
              <h3 className="m-0 text-sm font-medium text-muted-foreground tracking-wide">
                {t('landing.dashboard.keywordTrend')}
              </h3>
              <span className="rounded-full bg-badge px-2.5 py-1 text-[10px] font-medium text-badge-foreground">
                {t('landing.dashboard.badges.nearRealtime')}
              </span>
            </div>

            <AreaChart data={trendData} height={160} />

            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className={cn(innerCard, 'p-3.5')}>
                <div className="text-[11px] tracking-wide text-muted-foreground">
                  {t('landing.dashboard.cards.mentions')}
                </div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {t('landing.dashboard.cards.mentionsValue')}
                </div>
              </div>
              <div className={cn(innerCard, 'p-3.5')}>
                <div className="text-[11px] tracking-wide text-muted-foreground">
                  {t('landing.dashboard.cards.activeSources')}
                </div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {t('landing.dashboard.cards.activeSourcesValue')}
                </div>
              </div>
              <div className={cn(innerCard, 'p-3.5')}>
                <div className="text-[11px] tracking-wide text-muted-foreground">
                  {t('landing.dashboard.cards.freshness')}
                </div>
                <div className="mt-1 text-lg font-semibold text-foreground">
                  {t('landing.dashboard.cards.freshnessValue')}
                </div>
              </div>
            </div>
          </div>

          {/* Right sidebar panels */}
          <div className="grid gap-4 content-start">
            {/* Sentiment distribution */}
            <div className={cn(glassCard, 'relative overflow-hidden p-5')}>
              {isDark && (
                <BorderBeam duration="7s" delay="2s" color="var(--chart-particle)" />
              )}
              <div className="mb-4 flex items-center justify-between">
                <h3 className="m-0 text-sm font-medium text-muted-foreground">
                  {t('landing.dashboard.sentimentDistribution')}
                </h3>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase text-muted-foreground ring-1 ring-border">
                  {t('landing.dashboard.badges.beta')}
                </span>
              </div>
              <div className="space-y-3">
                {sentimentBars.map(({ labelKey, value, color }) => (
                  <div key={labelKey}>
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {t(`landing.dashboard.labels.${labelKey}`)}
                      </span>
                      <span className="font-medium text-foreground">{value}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-secondary">
                      <div
                        className="h-1 rounded-full transition-all duration-700"
                        style={{ width: `${value}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mb-0 mt-3.5 text-[11px] text-muted-foreground">
                {t('landing.dashboard.sentimentNote')}
              </p>

              {/* Inline mini volume chart */}
              <div className="mt-4">
                <div className="mb-2 text-[11px] tracking-wide text-muted-foreground">
                  {t('landing.dashboard.keywordTrend')}
                </div>
                <AreaChart
                  data={volumeData}
                  height={60}
                  fillColorStart="var(--chart-fill-start)"
                  fillColorEnd="var(--chart-fill-end)"
                />
              </div>
            </div>

            {/* Keyword watch */}
            <div className={cn(glassCard, 'p-5')}>
              <h3 className="m-0 text-sm font-medium text-muted-foreground">
                {t('landing.dashboard.keywordWatch')}
              </h3>
              <div className="mt-3.5 space-y-2">
                {keywords.map((item) => (
                  <div
                    key={item.key}
                    className={cn(
                      'flex items-center justify-between rounded-lg px-3 py-2 text-xs',
                      innerCard,
                    )}
                  >
                    <span className="font-medium text-foreground">{item.key}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground">{item.mentions}</span>
                      <span className="font-medium text-sentiment-positive">
                        {item.delta}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Monitoring status */}
            <div className={cn(glassCard, 'p-5')}>
              <h3 className="m-0 text-sm font-medium text-muted-foreground">
                {t('landing.dashboard.monitoringStatus')}
              </h3>
              <div className="mt-3.5 space-y-2">
                {[
                  {
                    icon: Clock3,
                    color: 'text-primary',
                    labelKey: 'freshness',
                    valueKey: 'freshnessValue',
                  },
                  {
                    icon: BellRing,
                    color: 'text-primary',
                    labelKey: 'alerts',
                    valueKey: 'alertsValue',
                  },
                  {
                    icon: Newspaper,
                    color: 'text-primary',
                    labelKey: 'news',
                    valueKey: 'newsValue',
                  },
                ].map(({ icon: Icon, color, labelKey, valueKey }) => (
                  <div
                    key={labelKey}
                    className={cn(
                      'flex items-center justify-between rounded-lg px-3 py-2 text-xs',
                      innerCard,
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={13} className={color} />
                      <span className="text-muted-foreground">
                        {t(`landing.dashboard.status.${labelKey}`)}
                      </span>
                    </div>
                    <span className="font-medium text-foreground">
                      {t(`landing.dashboard.status.${valueKey}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
